import {
  FormErrors,
  FormMapping,
  FormState,
  FormStructure,
  FormValidatorState,
  FormValuesConstraint,
  StructureProp,
  ValidatorReturn,
} from '../types';

import {asGroupNodes, asStepNodes} from './structure';

// Build/update `validation` state object
export const validateField = <
  T extends FormValuesConstraint<T>,
  S extends FormStructure<T>,
>(
  fieldName: keyof T,
  isValid: boolean,
  newValidationState: FormValidatorState<T, S>,
  mapping: FormMapping<T>,
  structure: StructureProp<T, S>,
): void => {
  newValidationState.byField[fieldName] = isValid;

  Object.keys(mapping.byStep).forEach((stepName) => {
    let stepIsValid = true;

    mapping.byStep[stepName].forEach((field: keyof T) => {
      stepIsValid = stepIsValid && !!newValidationState.byField[field];

      newValidationState.byStep[stepName] = {
        ...newValidationState.byStep[stepName],
        fields: {
          ...newValidationState.byStep[stepName]?.fields,
          [field]: newValidationState.byField[field],
        },
      };
    });

    newValidationState.byStep[stepName] = {
      fields: {}, // Default in case no fields
      ...newValidationState.byStep[stepName],
      isValid: stepIsValid,
    };
  });

  if (asGroupNodes<T>(structure)) {
    Object.keys(mapping.byGroup).forEach((groupName) => {
      let groupIsValid = true;

      Object.keys(mapping.byGroup[groupName]).forEach((stepName) => {
        groupIsValid =
          groupIsValid && !!newValidationState.byStep[stepName].isValid;

        newValidationState.byGroup[groupName] = {
          ...newValidationState.byGroup[groupName],
          steps: {
            ...newValidationState.byGroup[groupName]?.steps,
            [stepName]: newValidationState.byStep[stepName],
          },
        };
      });

      newValidationState.byGroup[groupName] = {
        steps: {}, // Default in case no steps
        ...newValidationState.byGroup[groupName],
        isValid: groupIsValid,
      };
    });
  }
};

const checkIfHasNoErrors = <T extends FormValuesConstraint<T>>(
  errors: FormErrors<T>,
  required?: Array<keyof T>,
) => {
  let hasNoErrors = true;

  required?.forEach((fieldName: keyof T) => {
    const hasError =
      typeof errors?.[fieldName] === 'string' && !!errors?.[fieldName]?.length;

    hasNoErrors = hasNoErrors && !hasError;
  });

  return hasNoErrors;
};

export const stepValidation = <
  T extends FormValuesConstraint<T>,
  S extends FormStructure<T>,
>(
  state: FormState<T, S>,
  structure: StructureProp<T, S>,
  step: string,
) => {
  const {validation, errors} = state;
  const isValid = !!validation.byStep?.[step]?.isValid;
  let hasRequirements = false;
  let hasNoErrors = true;

  const steps = asStepNodes<T>(structure);

  // Multi-step form
  if (steps?.[step]) {
    const {required} = steps[step];

    hasRequirements = !!required?.length;
    hasNoErrors = hasNoErrors && checkIfHasNoErrors(errors, required);
  }
  // Grouped multi-step form
  else if (!steps) {
    const groups = asGroupNodes<T>(structure);

    Object.keys(groups || {}).forEach((groupName) => {
      const groupStep = groups?.[groupName][step];

      if (groupStep) {
        hasRequirements = !!groupStep.required?.length;
        hasNoErrors =
          hasNoErrors && checkIfHasNoErrors(errors, groupStep.required);
      }
    });
  }

  return hasRequirements ? isValid && hasNoErrors : true;
};

export const groupValidation = <
  T extends FormValuesConstraint<T>,
  S extends FormStructure<T>,
>(
  state: FormState<T, S>,
  structure: StructureProp<T, S>,
  groupName: string,
) => {
  const {validation, errors} = state;
  const isValid = !!validation?.byGroup?.[groupName]?.isValid;
  let doesNotHaveRequirements = true;
  let hasNoErrors = true;

  const groups = asGroupNodes<T>(structure);

  if (groups) {
    Object.values(groups[groupName]).forEach(({required}) => {
      doesNotHaveRequirements = doesNotHaveRequirements && !required?.length;

      hasNoErrors = hasNoErrors && checkIfHasNoErrors(errors, required);
    });
  }

  return doesNotHaveRequirements ? true : isValid && hasNoErrors;
};

export const formValidation = <
  T extends FormValuesConstraint<T>,
  S extends FormStructure<T>,
>(
  state: FormState<T, S>,
  structure: StructureProp<T, S>,
) => {
  let isValid = true;

  const steps = asStepNodes<T>(structure);

  // Multi-step form
  if (steps) {
    Object.keys(steps).forEach((stepName) => {
      isValid = isValid && stepValidation(state, structure, stepName);
    });

    return isValid;
  }

  // Grouped multi-step form
  const groups = asGroupNodes<T>(structure);

  if (groups) {
    Object.keys(groups).forEach((groupName) => {
      isValid = isValid && groupValidation(state, structure, groupName);
    });
  }

  return isValid;
};

export const validatorIsPromise = (
  cb: ValidatorReturn | Promise<ValidatorReturn>,
): cb is Promise<ValidatorReturn> => {
  return !!cb && typeof (cb as Promise<ValidatorReturn>).then === 'function';
};
